import React, { useState } from 'react';
import styles from './UploadPage.module.css';
import MainLayout from '../../layouts/MainLayout';
import apiClient from '../../api/apiClient';
import {
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const ColorSlider = styled(Slider)(({ theme }) => ({
  height: 8,
  '& .MuiSlider-track': {
    border: 'none',
    backgroundColor: 'transparent', // Make track transparent
  },
  '& .MuiSlider-rail': {
    background: 'linear-gradient(90deg, #f44336 0%, #ffc107 50%, #4caf50 100%)',
    opacity: 1,
  },
  '& .MuiSlider-thumb': {
    height: 24,
    width: 24,
    backgroundColor: '#fff',
    border: '2px solid #1976d2',
    '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
      boxShadow: 'inherit',
    },
    '&:before': {
      display: 'none',
    },
  },
  '& .MuiSlider-valueLabel': {
    lineHeight: 1.2,
    fontSize: 12,
    background: 'unset',
    padding: 0,
    width: 32,
    height: 32,
    borderRadius: '50% 50% 50% 0',
    backgroundColor: '#1976d2',
    transformOrigin: 'bottom left',
    transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
    '&:before': { display: 'none' },
    '&.MuiSlider-valueLabelOpen': {
      transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
    },
    '& > *': {
      transform: 'rotate(45deg)',
    },
  },
}));

const client = apiClient('survey');

const filenameToTMap = {
  972958: 1,
  990032: 2,
  1011168: 3,
  1026323: 4,
  1033035: 5,
  1044743: 6,
  1048566: 7,
  1067657: 8,
};

const UploadPage = () => {
  const [file, setFile] = useState(null);
  const [t, setT] = useState('');
  const [threshold, setThreshold] = useState(1.0);
  const [loading, setLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);

    if (selectedFile) {
      // First, check filenameToTMap
      const matchedKey = Object.keys(filenameToTMap).find((key) =>
        selectedFile.name.includes(key)
      );
      let tValue = matchedKey ? filenameToTMap[matchedKey] : '';

      // If not found, check for T{int} pattern in filename
      if (tValue === '') {
        const tMatch = selectedFile.name.match(/T(\d+)/i);
        if (tMatch && tMatch[1]) {
          const tInt = parseInt(tMatch[1]);
          // Only assign if tInt is in allowed range
          if ([1, 2, 3, 4, 5, 6, 7, 8].includes(tInt)) {
            tValue = tInt;
          }
        }
      }

      setT(tValue);
      setThreshold(1.0);
    }
  };

  const handleTChange = (e) => {
    const val = e.target.value;
    setT(val === '' ? '' : parseInt(val));
  };

  const handleThresholdChange = (_, newValue) => {
    setThreshold(newValue);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || t === '') return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const response = await client.post(
        `/surveys/upload?t=${t}&threshold=${threshold}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      setUploadResult(response.data);
    } catch (error) {
      const detail = error?.response?.data?.detail || error.message;
      setUploadResult({ error: `Upload failed: ${detail}` });
    } finally {
      setLoading(false);
    }
  };

  const renderResults = () => {
    if (!uploadResult) return null;
    if (uploadResult.error)
      return <div className={styles.error}>{uploadResult.error}</div>;

    return (
      <div className={styles.results}>
        <div className={styles.summary}>
          <h3>Import Summary</h3>
          <div className={styles.statsGrid}>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Total Processed:</span>
              <span className={styles.statValue}>{uploadResult.total}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Successfully Imported:</span>
              <span className={styles.statValue}>{uploadResult.imported}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Already Existing:</span>
              <span className={styles.statValue}>{uploadResult.existing}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>No Matches:</span>
              <span className={styles.statValue}>
                {uploadResult.matchless.length}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Invalid Entries:</span>
              <span className={styles.statValue}>{uploadResult.invalid}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Errors:</span>
              <span className={styles.statValue}>{uploadResult.errors}</span>
            </div>
          </div>
        </div>

        {uploadResult.matchless.length > 0 && (
          <div className={styles.matchlessSection}>
            <h3>No Match Entries</h3>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell rowSpan={2}>Score</TableCell>
                    <TableCell rowSpan={2}>Input</TableCell>
                    <TableCell colSpan={3} align="center">
                      Closest Match
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Founder</TableCell>
                    <TableCell>Identifier</TableCell>
                    <TableCell>Existing Surveys for T={t}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...uploadResult.matchless]
                    .sort((a, b) => b.score - a.score)
                    .map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell>{entry.score}</TableCell>
                        <TableCell>
                          {Object.entries(entry.input).map(([key, value]) => (
                            <div key={key}>{value}</div>
                          ))}
                        </TableCell>
                        <TableCell>
                          {Object.entries(entry.closest_match)
                            .filter(([key]) => key !== 'identifier')
                            .map(([key, value]) => (
                              <div key={key}>
                                {Array.isArray(value)
                                  ? value.join(', ')
                                  : value}
                              </div>
                            ))}
                        </TableCell>
                        <TableCell>
                          {entry.closest_match &&
                          entry.closest_match.identifier ? (
                            entry.closest_match.identifier
                          ) : (
                            <span>None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {entry.existing_surveys_for_t &&
                          entry.existing_surveys_for_t.length > 0 ? (
                            <span>
                              lfdn: {entry.existing_surveys_for_t.join(', ')}
                            </span>
                          ) : (
                            <span>None</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        )}
      </div>
    );
  };

  return (
    <MainLayout>
      <div className={styles.container}>
        <h2 className={styles.title}>Upload CSV File</h2>
        <form onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label>Choose CSV file:</label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
            />
          </div>
          {!file && (
            <div className={styles.hintText}>
              It is recommended to upload files directly exported from Tivian.
              Compatibility can not be guaranteed for exports as CSV from third
              party software.
            </div>
          )}

          {file && (
            <div className={styles.formGroup}>
              <label>Survey Number:</label>
              <select value={t} onChange={handleTChange}>
                <option value="">Please Select</option>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((val) => (
                  <option key={val} value={val}>
                    {val}
                  </option>
                ))}
              </select>
            </div>
          )}

          {file && t !== '' && (
            <div className={styles.formGroup}>
              <label>Threshold:</label>
              <ColorSlider
                value={threshold}
                onChange={handleThresholdChange}
                min={0}
                max={1}
                step={0.01}
                marks={[
                  { value: 0, label: '0' },
                  { value: 0.5, label: '0.5' },
                  { value: 1, label: '1' },
                ]}
                valueLabelDisplay="auto"
              />
            </div>
          )}

          {file && t !== '' && (
            <div className={styles.formGroup}>
              <button type="submit" disabled={loading}>
                Upload
              </button>
            </div>
          )}
        </form>

        {loading && <div className={styles.spinner}></div>}
        {renderResults()}
      </div>
    </MainLayout>
  );
};

export default UploadPage;
